import { css } from '@emotion/react';
import { breakpoints, fonts } from '@birdi/theme';
import CrashScreenSVG from '@birdi/icons/crash-screen.svg';
import React, { useEffect } from 'react';
import { handle_global_error } from '@birdi/js-sdk';
import { isRouteErrorResponse, useRouteError } from 'react-router';

const buttonsStyle = css`
  min-width: 5em;
  padding: 0.5em 1em;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: solid 2px #0078c0;
  text-decoration: none;
  font-family: ${fonts.body};
  font-weight: 600;
`;

const CrashDisplay = () => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100vh;
      position: absolute;
      top: 0;
      left: 0;
      margin: 0;
      padding: 2em;
      box-sizing: border-box;
      font-family: ${fonts.body};
      background-color: #effbf5;
      color: #38385c;
      text-align: center;
    `}
  >
    <div
      css={css`
        max-width: 30em;
      `}
    >
      <h1
        css={css`
          margin: 0;
        `}
      >
        Something went wrong—but we're on it.
      </h1>
    </div>
    <div
      css={css`
        margin-top: 1.5em;
        svg {
          font-size: 12em;
        }
        @media (max-width: ${breakpoints.xs}) {
          svg {
            font-size: 8em;
          }
        }
      `}
    >
      <CrashScreenSVG />
    </div>
    <p
      css={css`
        font-size: 1.1em;
        line-height: 1.75em;
        margin: 1.5em;
        text-align: center;
        max-width: 30em;
      `}
    >
      The page didn't load as expected.
      <br />
      Try refreshing, or come back in a few minutes while we sort things out
      behind the scenes.
    </p>
    <div
      css={css`
        display: flex;
        flex-direction: row;
        gap: 2em;
      `}
    >
      <a
        css={css`
          ${buttonsStyle};
          background: #0078c0;
          color: white;
        `}
        href={'/'}
      >
        Head back home
      </a>
      <a
        css={css`
          ${buttonsStyle};
          background: white;
          color: #0078c0;
        `}
        href={
          'https://forms.monday.com/forms/8afaab1e0b5a0e2b42f1c7512ae206ee?r=use1'
        }
      >
        Report issue
      </a>
    </div>
  </div>
);

const CHUNK_RELOAD_KEY = 'chunkReloadAttempted';

/**
 * Attempts a one-shot navigation/reload to recover from a stale chunk after a
 * new deployment. Returns true if a navigation was triggered. If the key is
 * already set (i.e. we already tried once this session), returns false so the
 * caller can show the crash screen instead of looping forever.
 *
 * Pass `targetUrl` when the intended destination differs from the current URL
 * (e.g. React Router data-router lazy routes, where the URL hasn't committed
 * yet when the chunk error fires).
 */
export const reloadForChunkError = (targetUrl?: string): boolean => {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    if (targetUrl) {
      window.location.href = targetUrl;
    } else {
      window.location.reload();
    }
    return true;
  }
  return false;
};

export interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  { hasError: boolean }
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    const isChunkError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed');
    if (isChunkError && reloadForChunkError()) return;
    handle_global_error('global.crashscreen', error);
  }

  render() {
    const { children } = this.props;
    const { hasError } = this.state;

    return hasError ? <CrashDisplay /> : children;
  }
}

export const RouteErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    const isChunkError =
      error instanceof TypeError &&
      (error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Importing a module script failed'));

    if (isChunkError && reloadForChunkError()) return;

    if (isRouteErrorResponse(error)) {
      console.error('Router error:', error);
    } else handle_global_error('global.error', error);
  }, []);

  return <CrashDisplay />;
};
